import { LAYOUT_DEFAULTS } from "./carousel-options.logic";
import {
  slidePaddingValues,
  slidesVisibleValuesDesktop,
  slidesVisibleValuesMobile,
  slidesVisibleValuesTablet,
} from "./carousel.constants";

// Tile, layout, navigation and CTA inputs shared by the ProductCarousel and
// productRecommendations blocks (COM-426), so both Options tabs stay identical.

export const carouselLayoutInputs = [
  // ── Slides visible ────────────────────────────────────────────────────
  {
    name: "slidesVisible",
    type: "string",
    // "3.25" is a valid runtime peek value but intentionally omitted from the
    // editor enum — it is a code-only value, not selectable by content editors.
    enum: slidesVisibleValuesDesktop,
    defaultValue: LAYOUT_DEFAULTS.slidesVisible,
    helperText: "Slides visible on desktop",
  },
  {
    name: "slidesVisibleTablet",
    type: "string",
    enum: slidesVisibleValuesTablet,
    helperText:
      "Slides visible on tablet (leave unset to use the mobile setting)",
  },
  {
    name: "slidesVisibleMobile",
    type: "string",
    // "2.25" is a valid runtime peek value — omitted from editor enum.
    enum: slidesVisibleValuesMobile,
    defaultValue: LAYOUT_DEFAULTS.slidesVisibleMobile,
    helperText: "Slides visible on mobile",
  },
  // ── Peek ─────────────────────────────────────────────────────────────
  {
    name: "showPeek",
    type: "boolean",
    defaultValue: false,
    helperText: "Show a partial next slide on desktop",
  },
  {
    name: "showPeekTablet",
    type: "string",
    enum: [
      { label: "Same as mobile", value: "mobile" },
      { label: "On", value: "on" },
      { label: "Off", value: "off" },
    ],
    defaultValue: "mobile",
    helperText:
      "Partial next slide on tablet (with tablet slides unset, applies to the mobile count)",
  },
  {
    name: "showPeekMobile",
    type: "boolean",
    defaultValue: false,
    helperText: "Show a partial next slide on mobile",
  },
  // ── Slide padding ─────────────────────────────────────────────────────
  {
    name: "slidePadding",
    type: "string",
    enum: slidePaddingValues,
    helperText: "Gap between slides on desktop (0 = none, 6 = largest)",
  },
  {
    name: "slidePaddingTablet",
    type: "string",
    enum: slidePaddingValues,
    helperText:
      "Gap between slides on tablet (leave unset to use the mobile setting)",
  },
  {
    name: "slidePaddingMobile",
    type: "string",
    enum: slidePaddingValues,
    helperText: "Gap between slides on mobile (0 = none, 6 = largest)",
  },
];

export const navigationArrowInputs = [
  // ── Navigation arrows ─────────────────────────────────────────────────
  {
    name: "enableNavigationArrows",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Enable navigation arrows",
  },
  {
    name: "showNavigationArrowsMobile",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show navigation arrows on mobile",
    showIf: `options.get('enableNavigationArrows') === true`,
  },
  {
    name: "showNavigationArrowsTablet",
    type: "string",
    enum: [
      { label: "Same as mobile", value: "mobile" },
      { label: "Show", value: "on" },
      { label: "Hide", value: "off" },
    ],
    defaultValue: "mobile",
    friendlyName: "Show navigation arrows on tablet",
    showIf: `options.get('enableNavigationArrows') === true`,
  },
  {
    name: "showNavigationArrowsDesktop",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show navigation arrows on desktop",
    showIf: `options.get('enableNavigationArrows') === true`,
  },
];

export const tileAppearanceInputs = [
  // ── Tile appearance ───────────────────────────────────────────────────
  {
    name: "enableBasicView",
    type: "boolean",
    defaultValue: true,
    helperText: "Hide ratings for a compact tile layout",
  },
  {
    name: "productTileBorderColor",
    type: "color",
    helperText: "Leave unset for a white border",
  },
];

// ── Button navigation ─────────────────────────────────────────────────
export const buttonNavigationColorInput = {
  name: "ButtonNavigationColor",
  type: "color",
  friendlyName: "Button navigation color",
  helperText: "Pagination color; leave unset for the default mint",
};

export const ctaInputs = [
  // ── CTA ───────────────────────────────────────────────────────────────
  {
    name: "ctaText",
    type: "string",
    friendlyName: "CTA label",
    helperText: "Button label below the carousel (leave blank to hide)",
  },
  {
    name: "ctaHref",
    type: "string",
    friendlyName: "CTA URL",
    // Guard against undefined (untouched field) as well as empty string —
    // `undefined !== ''` is true, which would show the URL field before any
    // label exists and silently discard entered URLs at render time.
    showIf: `options.get('ctaText') && options.get('ctaText') !== ''`,
  },
];
